import { useState, useEffect, useCallback } from 'react';
import { enterMovesDown } from '../utils/cbsGridNav';
import api from '../api';
import { Gift, RefreshCw, Save, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import CageBalancingTabs from '../components/CageBalancingTabs';
import CageDatePicker from '../components/CageDatePicker';
import { useCageDate, CAGE_OFFSET, shiftDate } from '../contexts/CageDateContext';
import { useAuth } from '../contexts/AuthContext';

// ----------------------------------------------------------------
// Cage Free Play (promo chip) inventory
// ----------------------------------------------------------------
// Daily count of the promo/free-play chips the house holds, by denomination
// ($100/$25/$5, racks + units; rack = 100 chips). Produces the FP Chip Total
// and FP Liability (= total − FP float). Feeds the Chip Liability screen.

const FP_DENOMS = [10000, 2500, 500];
const fmtCents = (c) =>
  c == null ? '$0.00' : (Number(c) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const denomLabel = (d) => `$${(Number(d) / 100).toLocaleString('en-US')}`;
export default function CageFreePlay() {
  const { can } = useAuth();
  const canOperate = can('cbs:liability:enter');
  const { businessDate: date, setBusinessDate: setDate } = useCageDate(CAGE_OFFSET.CLOSED_DAY);
  const [rows, setRows] = useState({}); // denomCents -> { racks, units }
  const [fpFloatCents, setFpFloatCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/cbs/free-play', { params: { date } });
      const m = {};
      for (const d of res.data.denoms || []) m[d.denomCents] = { racks: d.racks, units: d.units };
      setRows(m);
      setFpFloatCents(res.data.fpFloatCents || 0);
      setLocked(!!res.data.locked);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load free play.');
    } finally {
      setLoading(false);
    }
  }, [date]);
  useEffect(() => {
    load();
  }, [load]);

  const val = (dc, k) => (rows[dc]?.[k] ?? '');
  const set = (dc, k, v) => setRows((p) => ({ ...p, [dc]: { ...(p[dc] || { racks: '', units: '' }), [k]: v } }));
  const denomTotal = (dc) => (Number(rows[dc]?.racks || 0) * 100 + Number(rows[dc]?.units || 0)) * dc;
  const fpTotal = FP_DENOMS.reduce((a, dc) => a + denomTotal(dc), 0);
  const fpLiability = fpTotal - fpFloatCents;

  const save = async () => {
    setSaving(true);
    try {
      const counts = FP_DENOMS.map((dc) => ({
        denomCents: dc,
        racks: Number(rows[dc]?.racks || 0),
        units: Number(rows[dc]?.units || 0),
      }));
      const res = await api.post('/admin/cbs/free-play', { businessDate: date, counts });
      setFpFloatCents(res.data.fpFloatCents || fpFloatCents);
      toast.success(`Saved — FP liability ${fmtCents(res.data.fpLiabilityCents)}.`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save free play.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <CageBalancingTabs />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <Gift className="text-amber-400" /> Free Play
          </h1>
          <p className="text-sm text-gray-400">
            Count the promo chips the house holds by denomination (rack = 100 chips). FP Liability =
            FP chip total − FP float. Flows into the Chip Liability reconciliation.
          </p>
          {/* The log is written the morning AFTER the gaming day it describes, so its
              row is dated one ahead of the day on screen. Saying which row to read
              costs a line here and has already cost two variances without it. */}
          <p className="mt-1 text-xs text-gray-500">
            This is {date}&rsquo;s closing position &mdash; counted the morning of{' '}
            <span className="text-gray-400">{shiftDate(date, 1)}</span>. In the Free Play log
            that is the row dated{' '}
            <span className="font-mono text-amber-300">{shiftDate(date, 1)}</span>, not {date}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CageDatePicker offsetDays={CAGE_OFFSET.CLOSED_DAY} showEntryDate />
          <button
            onClick={load}
            className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {locked && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-700/50 bg-emerald-900/20 p-3 text-sm text-emerald-300">
          <Lock size={16} /> Locked — Chip Liability is signed for {date}. A supervisor can unlock it on the Chip Liability tab.
        </div>
      )}
      {loading ? (
        <div className="p-8 text-center text-gray-400">Loading…</div>
      ) : (
        <div className="rounded-xl border border-gray-700 bg-gray-800 p-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" onKeyDown={enterMovesDown}>
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 pr-4 font-medium">Denom</th>
                  <th className="py-2 px-2 font-medium text-right">Racks (×100)</th>
                  <th className="py-2 px-2 font-medium text-right">Units</th>
                  <th className="py-2 pl-2 font-medium text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {FP_DENOMS.map((dc) => (
                  <tr key={dc} className="border-t border-gray-700/50">
                    <td className="py-1.5 pr-4 font-mono text-gray-200">{denomLabel(dc)}</td>
                    <td className="py-1.5 px-2 text-right">
                      <input
                        type="number"
                        min="0"
                        disabled={!canOperate || locked}
                        value={val(dc, 'racks')}
                        onChange={(e) => set(dc, 'racks', e.target.value)}
                        className="no-spin w-24 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-right text-white font-mono focus:outline-none focus:border-amber-500 disabled:opacity-50"
                      />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <input
                        type="number"
                        min="0"
                        disabled={!canOperate || locked}
                        value={val(dc, 'units')}
                        onChange={(e) => set(dc, 'units', e.target.value)}
                        className="no-spin w-24 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-right text-white font-mono focus:outline-none focus:border-amber-500 disabled:opacity-50"
                      />
                    </td>
                    <td className="py-1.5 pl-2 text-right font-mono text-gray-300">{fmtCents(denomTotal(dc))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 border-t border-gray-700 pt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">FP chip total</span>
              <span className="font-mono text-gray-200">{fmtCents(fpTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">FP float (beginning)</span>
              <span className="font-mono text-gray-400">{fmtCents(fpFloatCents)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-gray-700 pt-2 mt-1.5">
              <span className="text-xs uppercase tracking-wide text-gray-400">FP liability</span>
              <span className={`text-lg font-bold font-mono ${fpLiability > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {fmtCents(fpLiability)}
              </span>
            </div>
          </div>

          {canOperate && (
            <button
              onClick={save}
              disabled={saving || locked}
              className="mt-4 flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-black disabled:opacity-40"
            >
              <Save size={16} /> {saving ? 'Saving…' : 'Save free play'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
